import { useEffect, useState } from 'react';
import izenuAPI from '../../services/izenuAPI';
import { LINKED_TYPES, PhotoField, uploadUserPhoto, inputCls, EditSection, Field, Toggle, btnPrimary, btnSecondary } from './EditUserModal';

// Creates a platform account (table `user`) via admin/user/createUser.php.
// Artists and podcasters (acc_type 'creator') are linked to a distributor on creation.

const EMPTY = {
    acc_type: 'listener',
    name: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    artist_name: '',
    phone: '',
    distributor_id: '',
    verified: 'no',
};

export default function CreateUserModal({ distributors, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    // Set once the account exists, so a retry after a failed photo upload does not create it again.
    const [createdId, setCreatedId] = useState(null);

    const dirty = Object.keys(EMPTY).some(k => form[k] !== EMPTY[k]) || Boolean(photoFile);
    const hasDistributor = Boolean(LINKED_TYPES[form.acc_type]);

    const close = () => {
        if (saving) return;
        if (createdId) { onCreated(createdId); return; }
        if (dirty && !window.confirm('Os dados preenchidos vão perder-se. Fechar mesmo assim?')) return;
        onClose();
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            let userId = createdId;
            if (!userId) {
                const payload = { ...form };
                if (!hasDistributor) delete payload.distributor_id;
                const { data: res } = await izenuAPI.post('/admin/user/createUser.php', payload);
                if (!res.response) {
                    setError(res.message || 'Erro ao criar a conta');
                    setSaving(false);
                    return;
                }
                userId = res.user_id;
                setCreatedId(userId);
            }
            if (photoFile) {
                const photoError = await uploadUserPhoto(userId, photoFile);
                if (photoError) {
                    setError(`Conta criada (ID ${userId}), mas a foto falhou: ${photoError}`);
                    setSaving(false);
                    return;
                }
            }
            onCreated(userId);
            return;
        } catch (err) {
            console.log(err);
            setError('Erro ao criar a conta');
        }
        setSaving(false);
    };

    return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={close}>
        <form onSubmit={submit} onClick={e => e.stopPropagation()}
            className="relative flex w-full sm:max-w-2xl max-h-[94vh] sm:max-h-[90vh] flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white text-left shadow-xl">

            {/* Header */}
            <div className="flex items-center gap-3 border-b px-4 sm:px-6 py-4">
                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Novo utilizador</h3>
                    <p className="text-sm text-gray-500">Cria uma conta de ouvinte, artista ou podcaster.</p>
                </div>
                <button type="button" onClick={close} aria-label="Fechar" className="rounded-lg p-1.5 text-2xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
                <EditSection title="Conta">
                    <Field label="Tipo de conta">
                        <select className={inputCls} value={form.acc_type} onChange={e => set('acc_type', e.target.value)}>
                            <option value="listener">Ouvinte</option>
                            <option value="artist">Artista</option>
                            <option value="creator">Podcaster</option>
                        </select>
                    </Field>
                    {hasDistributor && (
                        <Field label="Distribuidora" hint={form.distributor_id ? undefined : 'Sem escolha, fica na distribuidora 1.'}>
                            <select className={inputCls} value={form.distributor_id} onChange={e => set('distributor_id', e.target.value)}>
                                <option value="">Escolher…</option>
                                {distributors.map(d => <option key={d.distributor_id} value={String(d.distributor_id)}>{d.name}</option>)}
                            </select>
                        </Field>
                    )}
                    <div className="sm:col-span-2">
                        <Toggle
                            checked={form.verified === 'yes'}
                            onChange={v => set('verified', v ? 'yes' : 'no')}
                            label="Verificado"
                            hint="Mostra o selo de verificado no perfil."
                        />
                    </div>
                </EditSection>

                <EditSection title="Identidade">
                    <PhotoField current={null} file={photoFile} onChange={setPhotoFile} />
                    <Field label="Nome de utilizador" hint="O @ público da conta.">
                        <input className={inputCls} required value={form.name} onChange={e => set('name', e.target.value)} />
                    </Field>
                    {hasDistributor && (
                        <Field label="Nome artístico" hint="É o nome mostrado no perfil.">
                            <input className={inputCls} required value={form.artist_name} onChange={e => set('artist_name', e.target.value)} />
                        </Field>
                    )}
                    <Field label="Primeiro nome">
                        <input className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} />
                    </Field>
                    <Field label="Apelido">
                        <input className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} />
                    </Field>
                </EditSection>

                <EditSection title="Contacto e acesso">
                    <Field label="Email" hint="É com ele que a pessoa entra na app.">
                        <input className={inputCls} type="email" required autoComplete="off" value={form.email} onChange={e => set('email', e.target.value)} />
                    </Field>
                    <Field label="Telefone">
                        <input className={inputCls} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </Field>
                    <Field label="Palavra-passe" hint="Pelo menos 6 caracteres." wide>
                        <div className="relative">
                            <input className={`${inputCls} pe-20`} type={showPassword ? 'text' : 'password'} required minLength={6} autoComplete="new-password"
                                value={form.password} onChange={e => set('password', e.target.value)} />
                            <button type="button" onClick={() => setShowPassword(v => !v)}
                                className="absolute inset-y-0 end-0 px-3 text-xs font-medium text-gray-500 hover:text-gray-800">
                                {showPassword ? 'Esconder' : 'Mostrar'}
                            </button>
                        </div>
                    </Field>
                </EditSection>
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse gap-2 border-t bg-gray-50 px-4 sm:px-6 py-3 sm:flex-row sm:items-center">
                <span className="text-xs text-gray-500 sm:me-auto">
                    {error && <span className="text-rose-600">{error}</span>}
                </span>
                <button type="button" onClick={close} disabled={saving} style={btnSecondary}>Cancelar</button>
                <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
                    {saving ? 'A criar…' : createdId ? 'Tentar foto de novo' : 'Criar conta'}
                </button>
            </div>
        </form>
    </div>
    );
}
